import {
  type SolarElectricalTopology,
  type SolarIdentifiedPanel,
} from './solarElectricalTopology';

export type SolarStringCheck = {
  status: 'ok' | 'check' | 'unknown';
  reasons: string[];
};

/**
 * Floor on the cold-weather Voc uplift, and the factor when nothing about the
 * site or panel is known. 1 + β(Tmin − 25) = 1.12 at β = −0.30 %/°C means
 * Tmin = −15 °C. A computed factor below it is raised to it; a higher one wins.
 */
export const SOLAR_COLD_VOC_FACTOR = 1.12;

/**
 * GB / IE crystalline-silicon factor when the panel's Voc coefficient is
 * unknown: "Voltage: Voc(stc) × 1.15" (Guide to the Installation of PV
 * Systems, 2nd ed., DECC / MCS / BRE 2012, §2.1.2; the same section gives
 * −15 °C as the low end of the UK temperature range).
 */
export const SOLAR_GB_COLD_VOC_FACTOR = 1.15;

/** Typical crystalline-silicon Voc temperature coefficient when the panel spec has none. */
export const SOLAR_DEFAULT_VOC_TEMPERATURE_COEFFICIENT_PCT_PER_C = -0.3;

/** NEC 690.8(A)(1) / IEC 62548: Isc × 1.25 for irradiance above STC. */
export const SOLAR_ISC_IRRADIANCE_FACTOR = 1.25;

/**
 * NEC Table 690.7(A), crystalline and multicrystalline silicon modules:
 * Voc correction for the lowest expected ambient, used when the module's
 * coefficient is unknown (690.7(A)(2)). `fromC` is the warm end of each 5 °C
 * band (24 to 20, 19 to 15, … −36 to −40).
 */
export const NEC_690_7_A_VOC_CORRECTION: ReadonlyArray<{
  fromC: number;
  toC: number;
  factor: number;
}> = [
  { fromC: 24, toC: 20, factor: 1.02 },
  { fromC: 19, toC: 15, factor: 1.04 },
  { fromC: 14, toC: 10, factor: 1.06 },
  { fromC: 9, toC: 5, factor: 1.08 },
  { fromC: 4, toC: 0, factor: 1.1 },
  { fromC: -1, toC: -5, factor: 1.12 },
  { fromC: -6, toC: -10, factor: 1.14 },
  { fromC: -11, toC: -15, factor: 1.16 },
  { fromC: -16, toC: -20, factor: 1.18 },
  { fromC: -21, toC: -25, factor: 1.2 },
  { fromC: -26, toC: -30, factor: 1.21 },
  { fromC: -31, toC: -35, factor: 1.23 },
  { fromC: -36, toC: -40, factor: 1.25 },
];

/**
 * The Table 690.7(A) factor for a design low. Fractional temperatures round
 * down (colder) into their band; 25 °C and above is 1.0. Below −40 °C the
 * table has no row and the last one applies (siteDesignMinTemperatureC never
 * returns lower).
 */
export const nec6907aVocFactor = (tMinC: number): number => {
  const t = Math.floor(tMinC);
  if (t >= 25) return 1;
  const row = NEC_690_7_A_VOC_CORRECTION.find((item) => t >= item.toC);
  return (row ?? NEC_690_7_A_VOC_CORRECTION.at(-1)!).factor;
};

type ColdVocRegion = 'GB' | 'US' | null;

/** GB/IE (accepting the account region `uk`) or US; anything else is null. */
const coldVocRegion = (countryCode?: string | null): ColdVocRegion => {
  const country = countryCode?.trim().toUpperCase();
  if (country === 'GB' || country === 'UK' || country === 'IE') return 'GB';
  if (country === 'US') return 'US';
  return null;
};

/** The GB / IE design low used with a known coefficient when the site has none. */
const GB_DESIGN_MIN_C = -15;

const knownCoefficient = (beta: number | null | undefined): beta is number =>
  beta != null && Number.isFinite(beta) && beta < 0;

/**
 * Cold-weather Voc factor on the STC rating, never below SOLAR_COLD_VOC_FACTOR.
 * - Panel coefficient known: 1 + β(Tmin − 25 °C) (IEC 62548 / NEC
 *   690.7(A)(1)); GB / IE default Tmin to −15 °C.
 * - Coefficient unknown: GB / IE 1.15 (MCS guide), US NEC Table 690.7(A) at
 *   the design low, elsewhere β = −0.30 %/°C at the design low.
 * - Nothing known: SOLAR_COLD_VOC_FACTOR.
 */
export const solarColdVocFactor = (input?: {
  siteMinTemperatureC?: number | null;
  panelVocTemperatureCoefficientPctPerC?: number | null;
  /** ISO alpha-2 (`GB`, `IE`, `US`) or the account region `uk` / `us`. */
  countryCode?: string | null;
}): number => {
  const region = coldVocRegion(input?.countryCode);
  const site = input?.siteMinTemperatureC;
  const siteMin = site != null && Number.isFinite(site) ? site : null;
  const beta = input?.panelVocTemperatureCoefficientPctPerC;
  const floor = (factor: number) => Math.max(SOLAR_COLD_VOC_FACTOR, factor);
  if (knownCoefficient(beta)) {
    const tMin = siteMin ?? (region === 'GB' ? GB_DESIGN_MIN_C : null);
    return tMin == null
      ? SOLAR_COLD_VOC_FACTOR
      : floor(1 + (beta / 100) * (tMin - 25));
  }
  if (region === 'GB') return floor(SOLAR_GB_COLD_VOC_FACTOR);
  if (siteMin == null) return SOLAR_COLD_VOC_FACTOR;
  if (region === 'US') return floor(nec6907aVocFactor(siteMin));
  return floor(
    1 +
      (SOLAR_DEFAULT_VOC_TEMPERATURE_COEFFICIENT_PCT_PER_C / 100) *
        (siteMin - 25)
  );
};

/** Cell temperature for the hot-weather MPP voltage check (°C). */
export const SOLAR_HOT_CELL_TEMPERATURE_C = 70;

/** Vmp as a share of Voc when the panel spec has no Vmp. */
export const SOLAR_DEFAULT_VMP_OVER_VOC = 0.8;

/** Shortest string Auto-string aims for, and the amber line when the MPPT minimum is unknown. */
export const SOLAR_MIN_PANELS_PER_STRING = 8;

const known = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

const format = (value: number, digits: number) =>
  Number(value.toFixed(digits)).toString();

const count = (value: number | null | undefined) =>
  value != null && Number.isFinite(value) && value >= 1 ? Math.floor(value) : 1;

/** Quick per-string sanity check against the inverter input limits. Never an approval. */
export const checkSolarString = (input: {
  panelCount: number;
  panelVocV?: number | null;
  panelIscA?: number | null;
  /** Default SOLAR_DEFAULT_VMP_OVER_VOC × Voc. */
  panelVmpV?: number | null;
  inverterMaxInputVoltageV?: number | null;
  /** Per-input short-circuit limit (catalogue `maxModuleIscA`). */
  inverterMaxIscA?: number | null;
  /** Per-MPPT short-circuit limit (catalogue `maxMpptIscA`); wins over the per-input one. */
  inverterMaxMpptIscA?: number | null;
  /** Lowest MPPT operating voltage (catalogue `minMpptVoltageV`). */
  inverterMinMpptVoltageV?: number | null;
  /** Default 1. */
  parallelStringsOnInput?: number;
  /** Strings in parallel on this string's MPPT, itself included. Default parallelStringsOnInput. */
  stringsOnMppt?: number;
  /** Site design low (°C); see siteDesignMinTemperatureC. */
  siteMinTemperatureC?: number | null;
  /** Panel Voc coefficient in %/°C (negative). Unknown uses the regional fallback. */
  panelVocTemperatureCoefficientPctPerC?: number | null;
  /** Picks the fallback when the coefficient is unknown; see solarColdVocFactor. */
  countryCode?: string | null;
  /** Default SOLAR_MIN_PANELS_PER_STRING; shorter strings are flagged while the MPPT minimum is unknown. */
  minPanelsPerString?: number;
}): SolarStringCheck => {
  const {
    panelCount,
    panelVocV,
    panelIscA,
    inverterMaxInputVoltageV,
    inverterMaxIscA,
    inverterMaxMpptIscA,
    inverterMinMpptVoltageV,
  } = input;
  if (!Number.isFinite(panelCount) || panelCount <= 0)
    return { status: 'unknown', reasons: ['No panels on this string'] };
  const parallel = count(input.parallelStringsOnInput);
  const onMppt = Math.max(parallel, count(input.stringsOnMppt ?? parallel));

  const failures: string[] = [];
  const missing: string[] = [];

  if (known(panelVocV) && known(inverterMaxInputVoltageV)) {
    const coldVoc = panelVocV * solarColdVocFactor(input) * panelCount;
    if (coldVoc > inverterMaxInputVoltageV)
      failures.push(
        `Cold-weather voltage ${Math.round(coldVoc)} V exceeds ${format(inverterMaxInputVoltageV, 0)} V`
      );
  } else {
    if (!known(panelVocV)) missing.push('Panel Voc unknown');
    if (!known(inverterMaxInputVoltageV))
      missing.push('Inverter max input voltage unknown');
  }

  // Per MPPT when the catalogue has the limit; otherwise per input, and
  // strings sharing an MPPT stay unchecked against its combined current.
  if (known(panelIscA) && known(inverterMaxMpptIscA)) {
    const current = panelIscA * SOLAR_ISC_IRRADIANCE_FACTOR * onMppt;
    if (current > inverterMaxMpptIscA)
      failures.push(
        `MPPT short-circuit current ${format(current, 1)} A exceeds ${format(inverterMaxMpptIscA, 1)} A`
      );
  } else if (known(panelIscA) && known(inverterMaxIscA)) {
    const current = panelIscA * SOLAR_ISC_IRRADIANCE_FACTOR * parallel;
    if (current > inverterMaxIscA)
      failures.push(
        `Short-circuit current ${format(current, 1)} A exceeds ${format(inverterMaxIscA, 1)} A`
      );
    if (onMppt > 1)
      missing.push(
        `${onMppt} strings share this MPPT · per-MPPT current limit unknown`
      );
  } else {
    if (!known(panelIscA)) missing.push('Panel Isc unknown');
    if (!known(inverterMaxIscA))
      missing.push('Inverter max input current unknown');
  }

  // Hot-weather MPP voltage at 70 °C cells against the MPPT minimum.
  const minPanels = Math.max(
    1,
    Math.floor(input.minPanelsPerString ?? SOLAR_MIN_PANELS_PER_STRING)
  );
  if (known(inverterMinMpptVoltageV)) {
    let vmp: number | null = null;
    if (known(input.panelVmpV)) vmp = input.panelVmpV;
    else if (known(panelVocV)) vmp = panelVocV * SOLAR_DEFAULT_VMP_OVER_VOC;
    if (vmp != null) {
      const beta = knownCoefficient(input.panelVocTemperatureCoefficientPctPerC)
        ? input.panelVocTemperatureCoefficientPctPerC
        : SOLAR_DEFAULT_VOC_TEMPERATURE_COEFFICIENT_PCT_PER_C;
      const hotVmp =
        vmp *
        (1 + (beta / 100) * (SOLAR_HOT_CELL_TEMPERATURE_C - 25)) *
        panelCount;
      if (hotVmp < inverterMinMpptVoltageV)
        failures.push(
          `Hot-weather MPP voltage ${Math.round(hotVmp)} V below the ${format(inverterMinMpptVoltageV, 0)} V MPPT minimum`
        );
    }
  } else if (panelCount < minPanels) {
    failures.push(
      `Below the ${minPanels}-panel minimum · MPPT minimum voltage unknown`
    );
  }

  if (failures.length) return { status: 'check', reasons: failures };
  if (missing.length) return { status: 'unknown', reasons: missing };
  return { status: 'ok', reasons: [] };
};

/** Spec keys the topology check reads; numbers from the saved snapshot or live catalogue. */
export type SolarStringSpecKey =
  | 'openCircuitVoltageV'
  | 'shortCircuitCurrentA'
  | 'vocTemperatureCoefficientPctPerC'
  | 'maxInputVoltageV'
  | 'maxModuleIscA'
  | 'maxMpptIscA'
  | 'minMpptVoltageV';

/**
 * One topology string's check as the Electrical tab badge, the single-line
 * diagram and the server automation show it: active panels only (included,
 * on an active roof), the worst panel spec on the string (highest Voc × cold
 * factor and highest Isc), strings sharing the input and the MPPT counted.
 * Callers handle unconnected, review and optimiser strings first.
 */
export const checkSolarTopologyString = (args: {
  string: SolarElectricalTopology['strings'][number];
  topology: SolarElectricalTopology;
  panels: readonly SolarIdentifiedPanel[];
  excludedFaceIds?: readonly number[];
  /** Raw spec number for an equipment selection, or null. */
  specOf: (
    selectionId: string | null | undefined,
    key: SolarStringSpecKey
  ) => number | null | undefined;
  siteMinTemperatureC?: number | null;
  countryCode?: string | null;
  minPanelsPerString?: number;
}): SolarStringCheck => {
  const { string, topology, specOf } = args;
  const excluded = new Set(args.excludedFaceIds ?? []);
  const panelById = new Map(args.panels.map((panel) => [panel.id, panel]));
  const active = string.panelIds.flatMap((id) => {
    const panel = panelById.get(id);
    return panel && panel.included && !excluded.has(panel.faceId)
      ? [panel]
      : [];
  });
  const selections = [
    ...new Set(active.map((panel) => panel.equipmentSelectionId)),
  ];
  // The panel spec giving the highest cold voltage sets Voc and its coefficient.
  let vocV: number | null = null;
  let beta: number | null = null;
  let worstCold = -Infinity;
  let iscA: number | null = null;
  for (const selectionId of selections) {
    const voc = specOf(selectionId, 'openCircuitVoltageV');
    const coefficient = specOf(selectionId, 'vocTemperatureCoefficientPctPerC');
    if (known(voc)) {
      const cold =
        voc *
        solarColdVocFactor({
          siteMinTemperatureC: args.siteMinTemperatureC,
          panelVocTemperatureCoefficientPctPerC: coefficient,
          countryCode: args.countryCode,
        });
      if (cold > worstCold) {
        worstCold = cold;
        vocV = voc;
        beta = knownCoefficient(coefficient) ? coefficient : null;
      }
    }
    const isc = specOf(selectionId, 'shortCircuitCurrentA');
    if (known(isc)) iscA = Math.max(iscA ?? 0, isc);
  }
  const unitSelection = topology.inverterInstances.find(
    (item) => item.id === string.inverterInstanceId
  )?.equipmentSelectionId;
  const sameUnit = topology.strings.filter(
    (item) =>
      item.inverterInstanceId != null &&
      item.inverterInstanceId === string.inverterInstanceId
  );
  return checkSolarString({
    panelCount: active.length,
    panelVocV: vocV,
    panelIscA: iscA,
    panelVocTemperatureCoefficientPctPerC: beta,
    inverterMaxInputVoltageV: specOf(unitSelection, 'maxInputVoltageV'),
    inverterMaxIscA: specOf(unitSelection, 'maxModuleIscA'),
    inverterMaxMpptIscA: specOf(unitSelection, 'maxMpptIscA'),
    inverterMinMpptVoltageV: specOf(unitSelection, 'minMpptVoltageV'),
    parallelStringsOnInput: sameUnit.filter(
      (item) => item.inputKey === string.inputKey
    ).length,
    stringsOnMppt: sameUnit.filter((item) => item.mpptKey === string.mpptKey)
      .length,
    siteMinTemperatureC: args.siteMinTemperatureC,
    countryCode: args.countryCode,
    ...(args.minPanelsPerString != null
      ? { minPanelsPerString: args.minPanelsPerString }
      : {}),
  });
};
